import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Skeleton } from "@sagot/ui";
import { useParams } from "react-router";
import { UpgradeCard, isPlanGateError } from "../shared/plan-gate";
import { useAgent } from "../shared/use-shared-queries";
import { useWebappDesign } from "./use-webapp-studio";
import { WebappEditor } from "./webapp-editor";

const NOT_FOUND = 404;
const K = "webappStudio.console";
const FLUSH = "-mx-4 -my-6 lg:-mx-8 lg:-my-8";

function StudioSkeleton() {
  return (
    <div className={`${FLUSH} flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row`} aria-hidden="true" data-testid="webapp-studio-skeleton">
      <div className="space-y-5 border-b border-line bg-surface p-5 lg:w-[420px] lg:shrink-0 lg:border-b-0 lg:border-r">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-4 w-[260px]" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="flex-1 bg-canvas p-4 lg:p-8">
        <Skeleton className="mx-auto h-[640px] max-w-5xl" />
      </div>
    </div>
  );
}

/** `/w/:ws/agents/:agentId/webapp/:tab?` — Web App Studio: the design of the hosted chat page. */
export function WebappStudioPage() {
  const { t } = useTranslation();
  const agentId = useParams().agentId ?? "";
  const design = useWebappDesign(agentId);
  const agent = useAgent(agentId);
  if (design.isPending) return <StudioSkeleton />;
  if (design.isError) {
    if (isPlanGateError(design.error)) return <UpgradeCard feature={t(`${K}.title`)} />;
    if (isApiError(design.error) && design.error.status === NOT_FOUND) {
      return (
        <div className="card mx-auto max-w-3xl">
          <h1 className="sr-only">{t(`${K}.title`)}</h1>
          <EmptyState icon="bot" title={t(`${K}.notFoundTitle`)} description={t(`${K}.notFoundBody`)} />
        </div>
      );
    }
    return (
      <div className="card mx-auto max-w-3xl">
        <h1 className="sr-only">{t(`${K}.title`)}</h1>
        <ErrorState title={t(`${K}.loadError`)} description={t(`${K}.loadErrorBody`)} onRetry={() => void design.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  return <WebappEditor key={agentId} agentId={agentId} agentName={agent.data?.name ?? ""} design={design.data} />;
}
